/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE COACH'S REVIEW OF A WORKOUT TRAVELS TO ITS NEXT OCCURRENCE — the read rule (`docs/CARRY-FEEDBACK-PLAN.md`, B). PURE.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The TWIN of `HybridTrainer/Services/CoachReviewCarryRule.swift` — THE SWIFT IS THE RULE. Held equal to it on every case
 * of `__fixtures__/coachReviewCarryCorpus.json`, which the Swift writes (`coachReviewCarryCorpus.test.ts`, the W66 method).
 *
 * On a workout, the client sees the coach's LATEST review of the same workout (its LINEAGE, `coalesce(root_workout_id,
 * workout_id)` of the assignment a session performed) if it was written after they last FINISHED that workout (a session of
 * the lineage, other than the one being shown, finished no later than `before`). Doing it again consumes it; a newer review
 * replaces it — one with no words clears it. A session with no lineage, or a review whose session is gone or unknown,
 * counts for nothing. A tie on `createdAt` breaks on the id, descending. Instants are milliseconds on both sides.
 */

export type CarryReview = { readonly id: string; readonly sessionId: string | null; readonly createdAt: string; readonly body: string };
export type CarrySession = { readonly id: string; readonly lineage: string | null; readonly finishedAt: string | null };

const ms = (iso: string): number => Date.parse(iso);

/** The last time, before this one, that the client finished a workout of this lineage (ms); null = never. */
export function lastFinished(lineage: string, sessions: readonly CarrySession[], currentSessionId: string | null, before: number): number | null {
  let best: number | null = null;
  for (const s of sessions) {
    if (s.id === currentSessionId || s.lineage !== lineage || s.finishedAt === null) continue;
    const f = ms(s.finishedAt);
    if (f > before) continue;
    if (best === null || f > best) best = f;
  }
  return best;
}

/** The review the client is shown on a workout of this lineage, or null (its words may be empty — it then clears). */
export function reviewCarryFor(
  lineage: string, reviews: readonly CarryReview[], sessions: readonly CarrySession[], currentSessionId: string | null, before: number,
): CarryReview | null {
  const since = lastFinished(lineage, sessions, currentSessionId, before);
  const lineageOf = new Map<string, string | null>();
  for (const s of sessions) if (!lineageOf.has(s.id)) lineageOf.set(s.id, s.lineage);
  let best: CarryReview | null = null;
  for (const r of reviews) {
    if (r.sessionId === null || (lineageOf.get(r.sessionId) ?? null) !== lineage) continue;
    if (since !== null && !(ms(r.createdAt) > since)) continue;
    if (best === null) { best = r; continue; }
    const a = ms(r.createdAt), b = ms(best.createdAt);
    if (a > b || (a === b && r.id.toLowerCase() > best.id.toLowerCase())) best = r;
  }
  return best;
}
